/**
 * EyeTestGridPanel.jsx — LIVE per-character eye-test grid (§6.6 A2).
 *
 * When a character node in the render-unit tree exposes its "Eye-test
 * grid" leaf and the BC selects it, the right-pane detail view takes
 * over with this panel. It is now fully wired to the R1 grid pipeline
 * (the same thunks the legacy standalone studio used): it fetches the
 * character's 9-cell CharSpeechSample grid, triggers a sequential
 * server-side render of every cell, polls job progress, and mirrors
 * per-cell status live via the shared <GridTable/> + <ProgressBar/>.
 *
 * Props:
 *   bookId          — the book the character belongs to (required to render).
 *   characterId     — CharacterVariation uuid (a.k.a. characterUuid server-side).
 *   characterLabel  — display label from the tree node.
 *   preflight       — optional PreflightResult for this unit (drives the
 *                     red-gate disable + banner).
 *   onGridComplete  — optional () => void, fired once on the grid job's
 *                     completion edge (was in-flight, now not). The studio
 *                     uses it to force-refetch the render tree so the tree's
 *                     eye-test status pills repaint live (§6.3-followup).
 *
 * Lifecycle notes (mirrors the legacy grid's hard-won behavior):
 *   - Poll effect deps are strictly [dispatch, job?.jobId, jobInFlight]

 *     to avoid the poll-thrash that an unstable job object caused.
 *   - clearRenderJob() on unmount AND on characterId change so a stale
 *     job never bleeds across characters.
 *   - 404-on-restart recovery: if progress polling fails because the
 *     in-memory job ledger was lost (process restart), clear the job and
 *     re-fetch the grid so the panel recovers instead of spinning.
 */

import React, { useCallback, useEffect, useMemo, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { Button, Header, Icon, Loader, Message, Segment } from "semantic-ui-react";

import {
  fetchCharacterGrid,
  startGridRender,
  pollGridProgress,
  clearRenderJob,
  selectCharacterGrid,
  selectCharacterGridLoading,
  selectCharacterGridError,
  selectRenderJob,
  selectRenderJobError,
  selectRenderJobInFlight,
} from "../../slices/voiceRenderingSlice";

import GridTable from "./GridTable";
import ProgressBar from "./ProgressBar";
import PreflightBanner from "./PreflightBanner";

const POLL_INTERVAL_MS = 1500;

// Heuristic for "the job we were polling no longer exists server-side"
// (e.g. process restart wiped the in-memory ledger). Kept permissive.
const isJobLostError = (err) =>
  typeof err === "string" && /404|not\s*found|no\s*such\s*job|expired/i.test(err);

const EyeTestGridPanel = ({
  bookId,
  characterId,
  characterLabel,
  preflight,
  onGridComplete,
}) => {
  const dispatch = useDispatch();


  const grid = useSelector((s) => selectCharacterGrid(s, characterId));
  const gridLoading = useSelector((s) => selectCharacterGridLoading(s, characterId));
  const gridError = useSelector((s) => selectCharacterGridError(s, characterId));

  const job = useSelector(selectRenderJob);
  const jobError = useSelector(selectRenderJobError);
  const jobInFlight = useSelector(selectRenderJobInFlight);

  // Fetch the grid whenever the (book, character) pair changes.
  useEffect(() => {
    if (bookId && characterId) {
      dispatch(fetchCharacterGrid({ bookId, characterUuid: characterId }));
    }
  }, [dispatch, bookId, characterId]);

  // Poll while a job is in flight. Strict deps — see header note.
  useEffect(() => {
    if (!job?.jobId) return undefined;
    if (!jobInFlight) return undefined;
    const id = setInterval(() => {
      dispatch(pollGridProgress(job.jobId));
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [dispatch, job?.jobId, jobInFlight]);

  // Fire onGridComplete once on the job's completion edge (was in-flight,
  // now not) so the studio can force-refetch the render tree — the tree's
  // eye-test status pills come from real RenderedGridCell counts and would
  // otherwise stay stale until the 30s TTL or a page re-enter.
  const prevJobInFlightRef = useRef(false);
  useEffect(() => {
    const justCompleted = prevJobInFlightRef.current && !jobInFlight;
    prevJobInFlightRef.current = jobInFlight;
    // Only treat it as a real completion when we actually had a job (guards
    // against the initial false→false and clearRenderJob resets).
    if (justCompleted && job?.jobId && typeof onGridComplete === "function") {
      onGridComplete();
    }
  }, [jobInFlight, job?.jobId, onGridComplete]);

  // 404-on-restart recovery: if polling errored because the job ledger
  // was lost, drop the stale job and re-pull the grid from persistence.

  useEffect(() => {
    if (jobError && isJobLostError(jobError)) {
      dispatch(clearRenderJob());
      if (bookId && characterId) {
        dispatch(fetchCharacterGrid({ bookId, characterUuid: characterId }));
      }
    }
  }, [dispatch, jobError, bookId, characterId]);

  // Clear any in-flight/finished job when leaving the character or unmounting.
  useEffect(() => () => { dispatch(clearRenderJob()); }, [dispatch, characterId]);

  const handleRenderAll = useCallback(() => {
    if (!bookId || !characterId) return;
    dispatch(startGridRender({ bookId, characterUuid: characterId }));
  }, [dispatch, bookId, characterId]);

  const cells = useMemo(
    () => (Array.isArray(grid?.cells) ? grid.cells : []),
    [grid]
  );
  const hasActiveClone = cells.some((c) => c.cloneIsActive);
  const gridReady = !!grid && !gridLoading && !gridError;
  const noCloneForCharacter = gridReady && cells.length > 0 && !hasActiveClone;
  const preflightRed = preflight?.status === "red";

  const renderDisabled =
    !bookId ||
    !characterId ||
    jobInFlight ||
    noCloneForCharacter ||
    preflightRed;

  return (
    <div className="eye-test-grid-panel">
      {preflight && <PreflightBanner preflight={preflight} />}

      <Segment className="etg-grid-segment">
        <Header as="h4" style={{ display: "flex", alignItems: "center" }}>
          <span>
            Eye-test grid{characterLabel ? ` — ${characterLabel}` : ""}
          </span>
          <span style={{ flex: 1 }} />
          <Button
            primary
            disabled={renderDisabled}
            loading={jobInFlight}
            onClick={handleRenderAll}
          >
            <Icon name="play" />
            {jobInFlight ? "Rendering…" : "Render all cells"}
          </Button>
        </Header>

        {noCloneForCharacter && (
          <Message
            warning
            size="tiny"
            content={
              "No active cloned voice for this character — every cell would " +
              "be skipped. Finish the Voice Cloning Pipeline first."
            }
          />
        )}

        <ProgressBar job={job} error={jobError} />

        {gridLoading && (
          <Loader active inline="centered" content="Loading grid…" />
        )}
        {gridError && <Message error compact content={gridError} />}
        {gridReady && <GridTable grid={grid} job={job} />}

        <div className="etg-footer-hint">
          <Icon name="info circle" /> Renders the character's 9 sample lines
          (3 emotions × 3 variants) through their cloned voice. Cell audio
          becomes playable as each cell completes.
        </div>
      </Segment>
    </div>
  );
};

export default EyeTestGridPanel;
